import {zodResolver} from "@hookform/resolvers/zod";
import {Loader2} from "lucide-react";
import {useMemo} from "react";
import {useForm} from "react-hook-form";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import {useTranslation} from "react-i18next";

import {Button} from "@/components/ui/button";
import {Form, FormControl, FormField, FormItem, FormLabel} from "@/components/ui/form";
import FormError from "@/components/ui/form-error";
import {cn} from "@/lib/utils";
import {PublicPageShell} from "@/src/modules/core/components";
import {useToast} from "@/src/modules/core/hooks";
import {LOGO_URL_DESKTOP} from "@/src/modules/core/utils/constants";

import {PasswordInput} from "../components";
import {useReset} from "../hooks";
import {createResetPasswordSchema, type ResetPasswordFormValues} from "../schemas";

const Reset = () => {
  const {t} = useTranslation("auth");
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const resetPasswordSchema = useMemo(() => createResetPasswordSchema(t), [t]);

  const form = useForm<ResetPasswordFormValues>({
    values: {
      token: token ?? "",
      password: "",
      password_confirm: "",
    },
    resolver: zodResolver(resetPasswordSchema),
  });

  const {mutateAsync: reset, isPending} = useReset();

  const handleSubmit = async (data: ResetPasswordFormValues) => {
    try {
      await reset(data);

      toast.success(t("reset.success"));
      navigate("/login");
    } catch (error) {
      //
    }
  };

  return (
    <PublicPageShell>
      <div className="rounded-lg bg-background p-6 shadow-lg sm:p-8">
        <div className="mb-8 flex flex-col items-center">
          <div className="flex size-16 items-center justify-center rounded-full">
            <img src={LOGO_URL_DESKTOP} alt="Image" height="100" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">{t("brand.name")}</h1>
          <p className="text-muted-foreground">{t("brand.tagline")}</p>
        </div>

        <h2 className="mb-6 text-center text-xl font-semibold text-foreground">
          {t("reset.title")}
        </h2>

        <Form {...form}>
          <form id="login-form" className="flex flex-col gap-4" onSubmit={form.handleSubmit(handleSubmit)}>
            <FormField
              control={form.control}
              name="password"
              render={({field, formState}) => (
                <FormItem>
                  <FormLabel>{t("reset.password")}</FormLabel>
                  <FormControl>
                    <PasswordInput {...field} disabled={isPending} />
                  </FormControl>
                  {formState.errors.password ? (
                    <FormError error={formState.errors.password.message} />
                  ) : null}
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password_confirm"
              render={({field, formState}) => (
                <FormItem>
                  <FormLabel>{t("reset.confirm_password")}</FormLabel>
                  <FormControl>
                    <PasswordInput {...field} disabled={isPending} />
                  </FormControl>
                  {formState.errors.password_confirm ? (
                    <FormError error={formState.errors.password_confirm.message} />
                  ) : null}
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className={cn(
                "w-full bg-primary hover:bg-primary/90",
                isPending && "cursor-not-allowed opacity-80",
              )}
              disabled={isPending}
            >
              {isPending ? (
                <>
                  {t("reset.submitting")}
                  <Loader2 className="mr-2 size-4 animate-spin" />
                </>
              ) : (
                t("reset.submit")
              )}
            </Button>
            <div className="flex justify-center">
              <Link to="/login" className="text-sm text-muted-foreground hover:underline">
                {t("forgot.back_to_login")}
              </Link>
            </div>
          </form>
        </Form>
      </div>

      <div className="mt-6 text-center text-xs text-muted-foreground">
        {t("brand.copyright", {year: new Date().getFullYear()})}
      </div>
    </PublicPageShell>
  );
};

export default Reset;
